// src/xr/session/XRSessionAuthority.ts
/**
 * XRSessionAuthority (v1.1)
 * ------------------------
 * Minimal XR authority gate with an inert-by-default policy.
 *
 * PURPOSE:
 * - Defines the single authority boundary for XR edit intents
 * - Prevents phantom imports / false confidence
 * - Enables controlled evolution toward multi-user XR authority
 *
 * DEFAULT POLICY (v1 behavior preserved):
 * - XR timeline edits are DISABLED
 * - All XR intents are rejected
 *
 * SECURITY PROPERTIES:
 * - This class NEVER dispatches actions
 * - This class NEVER mutates timeline state
 * - This class ONLY returns boolean allow/deny + issues/validates tokens
 *
 * HOW TO USE:
 * - Bridges (XRMarkerIntentBridge / XRRangeIntentBridge) should call:
 *     if (!authority.validateXRIntent(intent)) return;
 *     dispatch(...)
 */

export type XRAuthorityMode = "hosted" | "standalone";

/**
 * XRIntent is intentionally generic.
 * Bridges should pass minimal metadata so the authority layer can decide allow/deny.
 */
export interface XRIntent {
  /**
   * A coarse operation identifier for allow-listing and audits.
   * Keep this stable and low-cardinality.
   *
   * Examples:
   * - "marker/begin" | "marker/update" | "marker/remove"
   * - "range/begin"  | "range/update"  | "range/cancel" | "range/commit"
   */
  op?: string;

  /**
   * Optional lane binding for lane-scoped edits (Phase N2 Option 2).
   * Example: "n1" for NPC lane.
   */
  trackId?: string;

  meta?: {
    source?: "xr-user";
    clientId?: string;
    editToken?: string;
  };
}

type ClientRecord = {
  clientId: string;
  registeredAt: number;
  lastSeenAt: number;
  revoked: boolean;
};

type TokenRecord = {
  token: string;
  clientId: string;
  issuedAt: number;
  expiresAt: number;
  revoked: boolean;
};

export type XRSessionAuthorityOptions = {
  /**
   * Preserve v1 behavior: default is false.
   * Must be explicitly enabled to allow any XR edits.
   */
  enabled?: boolean;

  /**
   * Determines whether tokens are required.
   * - hosted: tokens required by default (safer multi-user posture)
   * - standalone: tokens optional by default (local/dev ergonomics)
   */
  requireToken?: boolean;

  /**
   * Token TTL in milliseconds (default: 2 minutes).
   */
  tokenTTLms?: number;

  /**
   * Allow-list of XR operations. If provided, only these ops are allowed.
   * If omitted, the default allow-list is used when enabled.
   */
  allowedOps?: string[];

  /**
   * Optional lane allow-list. If provided, only these trackIds may be edited from XR.
   * For Phase N2 Option 2 (envelope bands), this would commonly be ["n1"].
   */
  allowedTrackIds?: string[];

  /**
   * Optional hook to observe denials without throwing.
   * Use for debug logging, metrics, etc.
   */
  onDeny?: (reason: string, intent: XRIntent) => void;
};

function nowMs() {
  return Date.now();
}

function randomToken(): string {
  // Simple, dependency-free token. For production, you may swap to crypto.getRandomValues.
  return `xr_${Math.random().toString(16).slice(2)}_${Math.random().toString(16).slice(2)}`;
}

export class XRSessionAuthority {
  private readonly mode: XRAuthorityMode;

  private enabled: boolean;
  private requireToken: boolean;
  private tokenTTLms: number;

  private allowedOps: Set<string>;
  private allowedTrackIds: Set<string> | null;

  private readonly clients = new Map<string, ClientRecord>();
  private readonly tokens = new Map<string, TokenRecord>();

  private readonly onDeny?: (reason: string, intent: XRIntent) => void;

  constructor(mode: XRAuthorityMode, opts: XRSessionAuthorityOptions = {}) {
    this.mode = mode;

    // v1 behavior preserved: disabled by default
    this.enabled = Boolean(opts.enabled);

    // safer defaults by mode
    const defaultRequireToken = mode === "hosted";
    this.requireToken = opts.requireToken ?? defaultRequireToken;

    this.tokenTTLms = Math.max(5_000, opts.tokenTTLms ?? 120_000);

    // Default allow-list: intentionally narrow
    const DEFAULT_ALLOWED_OPS = [
      "marker/begin",
      "marker/update",
      "marker/remove",
      "range/begin",
      "range/update",
      "range/cancel",
      "range/commit",
    ];

    this.allowedOps = new Set(opts.allowedOps ?? DEFAULT_ALLOWED_OPS);

    this.allowedTrackIds =
      opts.allowedTrackIds && opts.allowedTrackIds.length > 0
        ? new Set(opts.allowedTrackIds)
        : null;

    this.onDeny = opts.onDeny;
  }

  /**
   * Optional runtime toggle (still inert unless explicitly enabled).
   * This does not retroactively validate already-issued tokens.
   */
  setEnabled(enabled: boolean): void {
    this.enabled = Boolean(enabled);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getMode(): XRAuthorityMode {
    return this.mode;
  }

  /**
   * Validate whether an XR-originated intent is allowed
   * to propose a timeline mutation.
   *
   * v1 default remains: returns false when disabled.
   */
  validateXRIntent(intent: XRIntent): boolean {
    // Hard gate: disabled means always deny (v1 behavior)
    if (!this.enabled) {
      this.deny("XR edits are disabled", intent);
      return false;
    }

    // Require XR source tag (bridges must set this)
    if (intent?.meta?.source !== "xr-user") {
      this.deny("Missing or invalid meta.source", intent);
      return false;
    }

    const clientId = intent?.meta?.clientId;
    if (!clientId || typeof clientId !== "string") {
      this.deny("Missing meta.clientId", intent);
      return false;
    }

    const client = this.clients.get(clientId);
    if (!client || client.revoked) {
      this.deny("Unregistered or revoked XR client", intent);
      return false;
    }

    // Op allow-list
    const op = intent.op ?? "";
    if (!op || !this.allowedOps.has(op)) {
      this.deny(`Operation not allowed: ${op || "(empty)"}`, intent);
      return false;
    }

    // Lane allow-list (Phase N2 Option 2: lane-scoped overlays)
    if (this.allowedTrackIds) {
      const trackId = intent.trackId ?? "";
      if (!trackId || !this.allowedTrackIds.has(trackId)) {
        this.deny(`TrackId not allowed: ${trackId || "(empty)"}`, intent);
        return false;
      }
    }

    // Token requirement (mode-sensitive)
    if (this.requireToken) {
      const token = intent?.meta?.editToken;
      if (!token || typeof token !== "string") {
        this.deny("Missing meta.editToken (token required)", intent);
        return false;
      }

      if (!this.validateToken(token, clientId)) {
        this.deny("Invalid/expired/revoked token", intent);
        return false;
      }
    }

    // Update liveness
    client.lastSeenAt = nowMs();
    this.clients.set(clientId, client);

    return true;
  }

  /**
   * Register an XR client.
   * v1: no-op; v1.1: stores a record for validation.
   */
  registerClient(clientId: string): void {
    if (!clientId) return;

    const t = nowMs();
    const existing = this.clients.get(clientId);

    if (existing) {
      existing.revoked = false;
      existing.lastSeenAt = t;
      this.clients.set(clientId, existing);
      return;
    }

    this.clients.set(clientId, {
      clientId,
      registeredAt: t,
      lastSeenAt: t,
      revoked: false,
    });
  }

  /**
   * Unregister an XR client.
   * v1: no-op; v1.1: marks revoked and revokes tokens for that client.
   */
  unregisterClient(clientId: string): void {
    if (!clientId) return;

    const rec = this.clients.get(clientId);
    if (rec) {
      rec.revoked = true;
      rec.lastSeenAt = nowMs();
      this.clients.set(clientId, rec);
    }

    // revoke all tokens issued to this client
    for (const tok of this.tokens.values()) {
      if (tok.clientId === clientId) {
        tok.revoked = true;
        this.tokens.set(tok.token, tok);
      }
    }
  }

  /**
   * Issue an XR edit token.
   * v1: returns null (no XR editing allowed)
   * v1.1: returns a short-lived token when enabled and client is registered.
   */
  issueEditToken(clientId: string): string | null {
    // Preserve v1 posture: if not enabled, no tokens.
    if (!this.enabled) return null;

    if (!clientId) return null;

    const client = this.clients.get(clientId);
    if (!client || client.revoked) return null;

    const token = randomToken();
    const issuedAt = nowMs();
    const expiresAt = issuedAt + this.tokenTTLms;

    const rec: TokenRecord = {
      token,
      clientId,
      issuedAt,
      expiresAt,
      revoked: false,
    };

    this.tokens.set(token, rec);
    return token;
  }

  /**
   * Optional: manually revoke a token (e.g., on authority transfer).
   */
  revokeToken(token: string): void {
    const rec = this.tokens.get(token);
    if (!rec) return;
    rec.revoked = true;
    this.tokens.set(token, rec);
  }

  /**
   * Optional: periodic cleanup for expired tokens.
   * Safe to call opportunistically.
   */
  sweepExpiredTokens(): void {
    const t = nowMs();
    for (const [token, rec] of this.tokens.entries()) {
      if (rec.expiresAt <= t) this.tokens.delete(token);
    }
  }

  private validateToken(token: string, clientId: string): boolean {
    const rec = this.tokens.get(token);
    if (!rec) return false;
    if (rec.revoked) return false;
    if (rec.clientId !== clientId) return false;
    if (rec.expiresAt <= nowMs()) return false;
    return true;
  }

  private deny(reason: string, intent: XRIntent): void {
    try {
      this.onDeny?.(reason, intent);
    } catch {
      // never throw from authority layer
    }
  }
}
